import { useState } from 'react';
import { AssistantConversationOutcome, IAssistantNewConversationRequest } from 'shared';

import { Button } from '../../../common/Atoms/Button';

export const outcomeLabels: Record<AssistantConversationOutcome, string> = {
  resolved: `Resolved`,
  partly: `Partly resolved`,
  not_resolved: `Not resolved`,
};

const options: { outcome: AssistantConversationOutcome; label: string; sub: string }[] = [
  { outcome: `resolved`, label: `Yes`, sub: `Fully sorted` },
  { outcome: `partly`, label: `Partly`, sub: `Some of it` },
  { outcome: `not_resolved`, label: `No`, sub: `Didn't help` },
];

interface IAssistantFinishChatProps {
  loading: boolean;
  onBack: () => void;
  /** Called without an outcome when the user skips rating */
  onSubmit: (rating: IAssistantNewConversationRequest) => void;
}

/** Replaces the composer when the user clicks Finish chat: rate the chat, then it is archived */
export function AssistantFinishChat({ loading, onBack, onSubmit }: IAssistantFinishChatProps) {
  const [outcome, setOutcome] = useState<AssistantConversationOutcome | null>(null);
  const [notes, setNotes] = useState(``);

  return (
    <div className={ `flex flex-col gap-3 border-t border-slate-200 bg-white p-4` }>
      <div className={ `flex flex-col gap-0.5` }>
        <span className={ `text-sm font-medium text-slate-800` }>{ `Did you get what you needed?` }</span>
        <span className={ `text-xs text-slate-500` }>{ `Your answer helps us improve the assistant.` }</span>
      </div>
      <div className={ `grid grid-cols-3 gap-2` }>
        {
          options.map(option => {
            const selected = outcome === option.outcome;

            return (
              <button
                key={ option.outcome }
                type={ `button` }
                aria-pressed={ selected }
                onClick={ () => setOutcome(option.outcome) }
                className={ `flex cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2.5 text-left text-sm font-medium shadow-xs transition ${selected ? `border-paidniceGreen-600 bg-paidniceGreen-50 text-paidniceGreen-800` : `border-slate-300 bg-white text-slate-800 hover:bg-slate-50`}` }
              >
                <span>{ option.label }</span>
                <span className={ `text-xs font-normal text-slate-500` }>{ option.sub }</span>
              </button>
            );
          })
        }
      </div>
      <textarea
        rows={ 2 }
        maxLength={ 2000 }
        value={ notes }
        onChange={ event => setNotes(event.target.value) }
        placeholder={ `Anything we should know? (optional)` }
        className={ `block w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 shadow-xs focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500/20` }
      />
      <div className={ `flex items-center justify-between gap-2` }>
        <button
          type={ `button` }
          disabled={ loading }
          onClick={ () => onSubmit({}) }
          className={ `cursor-pointer text-xs text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed` }
        >
          { `Skip and start a new chat` }
        </button>
        <div className={ `flex gap-2` }>
          <Button
            color={ `gray` }
            disabled={ loading }
            onClick={ onBack }
          >
            { `Back to chat` }
          </Button>
          <Button
            disabled={ !outcome || loading }
            loading={ loading }
            onClick={ () => outcome && onSubmit({ outcome, notes: notes.trim() || undefined }) }
          >
            { `Finish chat` }
          </Button>
        </div>
      </div>
    </div>
  );
}
